// The Costs page's stat cards and its period switch: total spend, tokens, finished steps and
// whose model key paid, over the chosen period.
import { Coins, Lightning, ListChecks, Wallet } from "@phosphor-icons/react";

import { day } from "../pages/format";
import { cardClass, MetricCard } from "../ui/Card";
import { cn } from "../ui/cn";
import { useT } from "../i18n";
import { compact, type CostReport, money, PERIODS, type Period, tokens } from "./model";
import { PaidSplitBar } from "./PaidSplitBar";

export function PeriodSwitch({ value, onChange }: { value: Period; onChange: (p: Period) => void }) {
  const t = useT().costs;
  return (
    <div role="group" aria-label={t.periodLabel} className="inline-flex rounded-lg border border-muted bg-gray-0 p-1 dark:bg-gray-50">
      {PERIODS.map((p) => (
        <button
          key={p}
          type="button"
          aria-pressed={value === p}
          onClick={() => onChange(p)}
          className={cn(
            "whitespace-nowrap rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors",
            value === p ? "bg-primary text-primary-foreground" : "text-gray-600 hover:text-gray-900",
          )}
        >
          {t.period[p]}
        </button>
      ))}
    </div>
  );
}

export function CostStats({ report }: { report: CostReport }) {
  const t = useT().costs;
  const since = report.since == null ? t.sinceFirst : t.since(day(report.since));
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <MetricCard title={t.totalSpend} metric={money(report.total_usd)} icon={<Coins weight="duotone" />} iconClassName="bg-primary-lighter text-primary-dark" info={since} />
      <MetricCard
        title={t.tokens}
        metric={compact(tokens(report))}
        icon={<Lightning weight="duotone" />}
        iconClassName="bg-blue-lighter text-blue-dark"
        info={t.inOut(compact(report.input_tokens), compact(report.output_tokens))}
      />
      <MetricCard
        title={t.agentSteps}
        metric={report.steps}
        icon={<ListChecks weight="duotone" />}
        iconClassName="bg-green-lighter text-green-dark"
        info={report.steps > 0 ? t.perStep(money(report.total_usd / report.steps)) : t.noneFinished}
      />
      <div className={cn(cardClass, "p-5")} role="group" aria-label={t.whoPaid}>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-orange-lighter text-orange-dark [&>svg]:h-5 [&>svg]:w-5">
            <Wallet weight="duotone" />
          </div>
          <div className="text-[13px] font-medium text-gray-500">{t.whoPaid}</div>
        </div>
        <PaidSplitBar className="mt-3" report={report} />
      </div>
    </div>
  );
}
